import { Link } from 'react-router';
import { ArrowRight, Crosshair, History, Scale, Trophy } from 'lucide-react';
import Eyebrow from '../ui/Eyebrow';
import { managerSlug } from '../../utils/slug';
import { CastFigure, Reveal } from '../../motion';

function format(value) {
  return Number.isFinite(value) ? value.toFixed(2) : '—';
}

function Side({ side, wins, average, align = 'left' }) {
  const right = align === 'right';
  return (
    <div className={`min-w-0 ${right ? 'text-right' : ''}`}>
      <div className={`paper aspect-[4/5] w-full max-w-[132px] border border-accent-gold/40 ${right ? 'ml-auto' : ''}`}>
        <CastFigure
          name={side.manager}
          trigger="inview"
          sequence={['shock', 'point']}
          className="absolute inset-0 w-full h-full"
        />
      </div>
      <div className="min-w-0 mt-3">
        <Link to={`/managers/${managerSlug(side.manager)}`} className="text-lg md:text-xl font-black hover:text-accent-gold truncate block">{side.manager}</Link>
        <div className="text-[9px] text-ink-tertiary tracking-[0.12em] truncate">{side.teamName}</div>
      </div>
      <div className={`mt-5 grid grid-cols-2 gap-2 ${right ? 'justify-items-end' : ''}`}>
        <div><div className="font-display text-3xl font-black text-accent-gold">{wins}</div><div className="text-[9px] text-ink-tertiary">H2H WINS</div></div>
        <div><div className="font-display text-3xl font-black">{format(average)}</div><div className="text-[9px] text-ink-tertiary">H2H AVG</div></div>
      </div>
      <div className="mt-4 text-[10px] text-ink-tertiary">
        {side.career.total_wins || 0} career wins · {side.career.playoff_appearances || 0} playoffs · {side.career.championships || 0} titles
      </div>
    </div>
  );
}

export default function WeekOneRivalryPreview({ previews = [] }) {
  if (!previews.length) return null;
  return (
    <section className="px-6 md:px-14 py-14 border-b border-border bg-bg-deep" data-testid="week-one-rivalries">
      <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <Eyebrow color="red">WEEK 1 RIVALRY ISSUE</Eyebrow>
          <h2 className="mt-3 font-display font-black uppercase leading-[0.92] tracking-normal" style={{ fontSize: 'clamp(34px, 5vw, 72px)' }}>
            History is already<br /><span className="text-accent-gold">on the scoreboard.</span>
          </h2>
          <p className="mt-4 max-w-3xl text-sm md:text-base text-ink-secondary leading-relaxed italic">
            No roster projections before the draft. These opening-week previews use completed regular-season meetings and validated career totals through 2025.
          </p>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-black tracking-[0.16em] text-accent-gold"><History size={16} /> 875-GAME ARCHIVE</div>
      </Reveal>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {previews.map((preview, index) => (
          <Reveal key={preview.id} delay={80 + (index % 2) * 60}>
            <article className="bg-bg-surface border-t-[3px] border-accent-gold overflow-hidden">
            <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
              <div className="text-[9px] tracking-[0.2em] font-black text-accent-gold">WEEK 1 · MATCHUP {preview.matchupNumber}</div>
              <div className="text-[9px] text-ink-tertiary">{preview.games} HISTORICAL MEETINGS</div>
            </div>
            <div className="grid grid-cols-[minmax(0,1fr)_54px_minmax(0,1fr)] gap-3 items-start p-5 md:p-6">
              <Side side={preview.home} wins={preview.homeWins} average={preview.homeAverage} />
              <div className="flex flex-col items-center pt-2"><div className="font-display text-2xl font-black text-ink-tertiary">VS</div><Scale size={18} className="mt-3 text-accent-gold" /></div>
              <Side side={preview.away} wins={preview.awayWins} average={preview.awayAverage} align="right" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 border-t border-border">
              <Receipt icon={Trophy} label="Historical edge" copy={preview.headline} />
              <Receipt icon={History} label="Last meeting" copy={preview.receipt} />
              <Receipt icon={Crosshair} label="Pressure point" copy={preview.closest ? `Closest meeting: ${Math.abs(preview.closest.margin).toFixed(2)} points in ${preview.closest.year}.` : preview.stakes} />
            </div>
            <div className="border-t border-border px-5 py-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-[10px] text-ink-tertiary italic">Career stakes: {preview.stakes}</p>
              <Link to={`/managers/${managerSlug(preview.leader || preview.home.manager)}`} className="inline-flex items-center gap-1 text-[9px] font-black tracking-[0.16em] text-accent-gold">OPEN PROFILE <ArrowRight size={13} /></Link>
            </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Receipt({ icon: Icon, label, copy }) {
  return <div className="p-4 md:border-l border-border first:border-l-0"><div className="flex items-center gap-2 text-[9px] tracking-[0.16em] font-black text-accent-gold"><Icon size={14} />{label.toUpperCase()}</div><p className="mt-2 text-[11px] text-ink-secondary leading-relaxed italic">{copy}</p></div>;
}
